import {useTrans} from '@ui/i18n/use-trans';
import {useSettings} from '@ui/settings/use-settings';
import clsx from 'clsx';
import {ReactNode} from 'react';

const BRAND_SITE_URL = 'https://qla.dev/';

interface LogoProps {
  color?: 'dark' | 'light';
  className?: string;
  logoType?: 'wide' | 'compact' | 'auto';
  mobileLogoType?: 'wide' | 'compact';
  size: string;
}
export function Logo({
  color = 'light',
  className: propsClassName,
  logoType = 'auto',
  mobileLogoType = 'compact',
  size,
}: LogoProps) {
  const className = clsx(propsClassName, size);

  if (logoType === 'compact') {
    return <CompactLogo color={color} className={className} />;
  } else if (logoType === 'wide') {
    return <WideLogo color={color} className={className} />;
  }

  return (
    <AutoLogo
      color={color}
      className={className}
      mobileLogoType={mobileLogoType}
    />
  );
}

interface CompactLogoProps {
  className?: string;
  color: LogoProps['color'];
}
function CompactLogo({color, className}: CompactLogoProps) {
  const {branding} = useSettings();

  // fallback to light logo if dark logo is not available
  const src =
    color === 'dark' && branding.logo_dark_mobile
      ? branding.logo_dark_mobile
      : branding.logo_light_mobile;

  if (!src) return null;

  return (
    <WrapperLink className={className}>
      <img src={src} className="block h-auto w-full" alt="" />
    </WrapperLink>
  );
}

interface AutoLogoProps extends CompactLogoProps {
  mobileLogoType: NonNullable<LogoProps['mobileLogoType']>;
}
function AutoLogo({color, className, mobileLogoType}: AutoLogoProps) {
  const {branding} = useSettings();

  let wideLogo: string;
  let compactLogo: string;
  if (color === 'light') {
    wideLogo = branding.logo_light;
    compactLogo = branding.logo_light_mobile;
  } else {
    wideLogo = branding.logo_dark;
    compactLogo = branding.logo_dark_mobile;
  }

  const mobileLogo =
    mobileLogoType === 'wide' ? wideLogo || compactLogo : compactLogo || wideLogo;

  if (!wideLogo && !mobileLogo) {
    return null;
  }

  return (
    <WrapperLink className={className}>
      <picture className="block w-full">
        <source srcSet={mobileLogo || wideLogo} media="(max-width: 768px)" />
        <source srcSet={wideLogo || mobileLogo} media="(min-width: 768px)" />
        <img src={wideLogo || mobileLogo} className="block h-auto w-full" alt="" />
      </picture>
    </WrapperLink>
  );
}

function WideLogo({color, className}: CompactLogoProps) {
  const {branding} = useSettings();

  const src =
    color === 'dark' && branding.logo_dark
      ? branding.logo_dark
      : branding.logo_light;

  if (!src) return null;

  return (
    <WrapperLink className={className}>
      <img src={src} className="block h-auto w-full" alt="" />
    </WrapperLink>
  );
}

interface WrapperLinkProps {
  className?: string;
  children: ReactNode;
}
function WrapperLink({className, children}: WrapperLinkProps) {
  const {trans} = useTrans();

  return (
    <a
      href={BRAND_SITE_URL}
      target="_blank"
      rel="noreferrer"
      className={clsx('block max-w-full flex-shrink-0', className)}
      aria-label={trans({message: 'Open qla.dev'})}
    >
      {children}
    </a>
  );
}
